import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  derivePairedBuildQaPreview,
  derivePairedReviewApprovalDelivery,
  derivePairedSafeEditPresentation,
  selectAgencyAccessIntakeReadinessStartHere,
  selectAgencyTodayStartHere,
  sitePackageIdFromRecipeId,
  type AccessIntakeReadinessPresentationInput,
  type AgencyPortfolioSummaryV1,
  type AgencyTodayStartHereInput,
  type CanonicalPresentationModelV1,
  type CanonicalPrimaryAction,
  type ClientLinkCreateResultV1,
  type StudioPackageResolutionV1,
} from "@enrein/shared-contracts";
import type { AgencyPublicScanOpportunityV1 } from "@enrein/shared-contracts/contracts/agency-unlock";
import { createStudioApiClient } from "../../api.js";
import { clientLinkUrl } from "../../client-link-url.js";
import { readClientLinkSession, saveClientLinkSession } from "../../client-link-session-store.js";
import { saveProjectDisplayName } from "../../project-display-name-store.js";
import { readStudioRuntimeConfig } from "../../env.js";
import { resolveSharePreviewUrl } from "../../resolve-share-preview-url.js";
import { usePolling } from "../../usePolling.js";
import {
  agencyEnterProject,
  confirmFactsAndBuildPreview,
  createExternalProofProject,
  createIntakeLinkProject,
  createRelaunchScanProject,
  formatWorkflowError,
  type PackageEligibilityBlocked,
} from "../studio-shared/workflow/studio-workflow.js";
import { sitePackageDisplayLabel } from "../studio-shared/site-package-labels.js";
import {
  PREVIEW_UNAVAILABLE_MESSAGE,
  getPreviewOpenBlockReason,
  logPreviewOpenGuardDebug,
  openBlobPreviewInNewTab,
  type PreviewOpenGuardInput,
} from "../studio-shared/preview-open-guard.js";
import {
  CLIENT_SHARE_QA_WARN_PRIMARY_CTA,
  getClientShareBlockReason,
  isQaWarnRunStatus,
  shouldOverrideHeroForQaWarn,
  type ClientShareGuardInput,
} from "../studio-shared/share-guard.js";
import {
  deriveCommandCenterStats,
  deriveSimpleProjectFacts,
  humanIntakeStatus,
  latestRun,
  mapLifecycleViewToPhaseState,
  isHandoffReadyDepartureKit,
  resolveStudioHasPreview,
} from "../studio-shared/orchestration/project-phase.js";
import { useMissionControl } from "../studio-shared/orchestration/useMissionControl.js";
import { useStudioProjectSnapshot } from "../studio-shared/orchestration/useStudioProjectSnapshot.js";
import { useStudioProjectsEnrichment } from "../studio-shared/orchestration/useStudioProjects.js";
import {
  deriveV10InboxRows,
  deriveV10QueueSections,
  deriveV10SectionProgress,
  deriveV10SuggestedSection,
  mapProjectSummaryToListItem,
  mapScanOpportunityToRow,
  resolveProjectDisplayName,
} from "./adapters/v10-project-adapters.js";
import { mapCanonicalIntentToStudioActionId } from "./adapters/v10-presentation-action-map.js";
import type { V10NewProjectFormState } from "./adapters/v10-new-project-form.js";
import { V10_EMPTY_NEW_PROJECT_FORM, validateV10NewProjectForm, v10FormToStudioInput } from "./adapters/v10-new-project-form.js";
import type { V10ProjectMode } from "./adapters/v10-phase-model.js";
import { v10ProjectRoute, v10NewWorkSheet } from "./adapters/v10-button-endpoints.js";
import { pushV10Route, parseV10Route } from "./routing/v10-routing.js";
import type { V10RoutePatch, V10RouteState } from "./routing/v10-routing.js";
import { V10Shell } from "./V10Shell.js";
import type { StudioQaIssue } from "./components/V10ResolveIssuesPanel.js";
import {
  isProjectPackageSnapshotReady,
  resolveAgencyRequestedForBuild,
  resolveAuthoritativeGraphPackageId,
  syncRequestedPackageForProject,
} from "./v10-project-package-state.js";
import "./styles/v10-fonts.css";
import "./styles/v10-ui.css";

type StudioPhaseState = ReturnType<typeof mapLifecycleViewToPhaseState>;

/**
 * Bind the Studio hero to a server-derived Agency presentation model. Presentation copy,
 * condition and primary action come from the canonical model — Studio never re-derives them.
 */
function heroFromAgencyPresentation(
  base: StudioPhaseState,
  agency: CanonicalPresentationModelV1,
): StudioPhaseState & { readonly heroPresentation: CanonicalPresentationModelV1 } {
  const mappedActionId = mapCanonicalIntentToStudioActionId(agency.primaryAction?.intent);
  return {
    ...base,
    nowTitle: agency.title,
    nowCopy: agency.reason,
    primaryCta: agency.primaryAction?.label?.trim() || base.primaryCta,
    primaryActionId: mappedActionId ?? base.primaryActionId,
    primaryDisabled: agency.primaryAction == null ? true : base.primaryDisabled,
    heroPresentation: agency,
  };
}

export function EnreinV10App({ agencyId }: { readonly agencyId: string }) {
  const config = useMemo(() => readStudioRuntimeConfig(), []);
  const api = useMemo(() => createStudioApiClient(config, { agencyId }), [agencyId, config]);
  const [route, setRoute] = useState<V10RouteState>(() => parseV10Route(window.location.search));
  const [toast, setToast] = useState<string | null>(null);
  const [workflowBusy, setWorkflowBusy] = useState(false);
  const [validationIssues, setValidationIssues] = useState<readonly string[]>([]);
  const [fastPollUntil, setFastPollUntil] = useState<number | null>(null);
  const [lastClientLink, setLastClientLink] = useState<ClientLinkCreateResultV1 | null>(null);
  const [handoffError, setHandoffError] = useState<string | null>(null);
  const [newProjectForm, setNewProjectForm] = useState<V10NewProjectFormState>(V10_EMPTY_NEW_PROJECT_FORM);
  const [scanOpportunities, setScanOpportunities] = useState<readonly AgencyPublicScanOpportunityV1[]>([]);
  const [scansLoading, setScansLoading] = useState(false);
  const [studioPreviewHtml, setStudioPreviewHtml] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [qaWarnings, setQaWarnings] = useState<readonly string[]>([]);
  const [qaIssues, setQaIssues] = useState<readonly StudioQaIssue[]>([]);
  const [importQueueAvailable, setImportQueueAvailable] = useState(false);
  const [warnShareAcknowledged, setWarnShareAcknowledged] = useState(false);
  const [packageResolution, setPackageResolution] = useState<StudioPackageResolutionV1 | null>(null);
  const [packageEligibilityBlocked, setPackageEligibilityBlocked] =
    useState<PackageEligibilityBlocked | null>(null);
  const [requestedSitePackageId, setRequestedSitePackageId] = useState("package-standard-5");
  const [previewRoutes, setPreviewRoutes] = useState<readonly string[]>(["/"]);
  const [activePreviewRoute, setActivePreviewRoute] = useState("/");
  const [apiHealth, setApiHealth] = useState<"checking" | "ok" | "unreachable">("checking");
  const [scansError, setScansError] = useState<string | null>(null);
  const userPickedSectionRef = useRef(false);
  const lastSuggestedSectionRef = useRef<string | null>(null);
  const lastProjectForRequestedRef = useRef<string | null>(null);
  const explicitPackageForProjectRef = useRef<string | null>(null);
  const generationOptionsRef = useRef<{ sitePackageId: string; lensId: string }>({
    sitePackageId: "package-standard-5",
    lensId: "lens-flat-2",
  });
  const [lastSyncedProjectId, setLastSyncedProjectId] = useState<string | null>(null);

  const selectedProjectId = route.view === "project" ? route.projectId : null;
  const pollMs =
    agencyId.length > 0 ? (fastPollUntil && Date.now() < fastPollUntil ? 2000 : config.pollIntervalMs) : 0;

  const loadProjectSummaries = useCallback(() => api.listProjectSummaries(), [api]);
  const projectsState = usePolling(loadProjectSummaries, pollMs);
  const { missionControlState, scanOpportunitiesState } = useMissionControl(api, pollMs);
  const enrichment = useStudioProjectsEnrichment(api, projectsState);
  const snapshot = useStudioProjectSnapshot(api, selectedProjectId);
  const [portfolioSummary, setPortfolioSummary] = useState<AgencyPortfolioSummaryV1 | null>(null);
  const [portfolioSummaryLoading, setPortfolioSummaryLoading] = useState(false);
  const [portfolioSummaryError, setPortfolioSummaryError] = useState<string | null>(null);

  useEffect(() => {
    if (route.view !== "portfolio") return;
    let cancelled = false;
    setPortfolioSummaryLoading(true);
    setPortfolioSummaryError(null);
    void api
      .getAgencyPortfolioSummary()
      .then((data) => {
        if (!cancelled) setPortfolioSummary(data);
      })
      .catch((error: unknown) => {
        if (!cancelled) setPortfolioSummaryError(formatWorkflowError(error));
      })
      .finally(() => {
        if (!cancelled) setPortfolioSummaryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, route.view]);

  useEffect(() => {
    void fetch(`${config.apiBaseUrl || ""}/health`)
      .then((res) => setApiHealth(res.ok ? "ok" : "unreachable"))
      .catch(() => setApiHealth("unreachable"));
  }, [config.apiBaseUrl]);

  const activeGraphProjectId = snapshot.activeGraph?.businessGraph.projectId;
  const authoritativeGraphPackageId = resolveAuthoritativeGraphPackageId({
    selectedProjectId,
    graphProjectId: activeGraphProjectId,
    graphPackageId: snapshot.activeGraph?.businessGraph.layout?.sitePackageId,
  });
  const projectPackageSnapshotReady = isProjectPackageSnapshotReady({
    selectedProjectId,
    lastSyncedProjectId,
    projectLoading: snapshot.projectLoading,
    graphProjectId: activeGraphProjectId,
  });
  const effectiveWorkflowBusy =
    workflowBusy || (selectedProjectId != null && !projectPackageSnapshotReady);

  useEffect(() => {
    if (!selectedProjectId) {
      lastProjectForRequestedRef.current = null;
      setLastSyncedProjectId(null);
      explicitPackageForProjectRef.current = null;
      setPackageEligibilityBlocked(null);
      setWarnShareAcknowledged(false);
      setPackageResolution(null);
      return;
    }
    const projectChanged = lastProjectForRequestedRef.current !== selectedProjectId;
    if (projectChanged) {
      explicitPackageForProjectRef.current = null;
      setPackageEligibilityBlocked(null);
      setWarnShareAcknowledged(false);
      setPackageResolution(null);
    }
    const synced = syncRequestedPackageForProject({
      selectedProjectId,
      lastSyncedProjectId: lastProjectForRequestedRef.current,
      graphProjectId: activeGraphProjectId,
      graphPackageId: snapshot.activeGraph?.businessGraph.layout?.sitePackageId,
      currentRequestedPackageId: generationOptionsRef.current.sitePackageId,
      hasExplicitSelectionForCurrentProject:
        explicitPackageForProjectRef.current === selectedProjectId,
    });
    lastProjectForRequestedRef.current = synced.lastSyncedProjectId;
    setLastSyncedProjectId(synced.lastSyncedProjectId);
    if (synced.changed) {
      generationOptionsRef.current.sitePackageId = synced.requestedPackageId;
      setRequestedSitePackageId(synced.requestedPackageId);
    }
  }, [
    selectedProjectId,
    activeGraphProjectId,
    snapshot.activeGraph?.businessGraph.layout?.sitePackageId,
  ]);

  useEffect(() => {
    if (selectedProjectId) {
      // Preserve deep-linked / explicit section (e.g. ?section=live-care) so phase
      // auto-suggest does not immediately overwrite requirement-specific navigation.
      const sectionFromUrl = parseV10Route(window.location.search).section;
      userPickedSectionRef.current = Boolean(sectionFromUrl);
      lastSuggestedSectionRef.current = null;
    }
  }, [selectedProjectId]);

  const syncEnrichmentFromSnapshot = useCallback(
    (projectId: string, snap: Awaited<ReturnType<typeof snapshot.reloadSelectedProject>>) => {
      if (!snap) return;
      enrichment.patchProjectEnrichment(projectId, {
        review: snap.review,
        departureKit: snap.departureKit,
        businessName: snap.businessName,
      });
    },
    [enrichment],
  );

  const reloadProjectSnapshot = useCallback(async () => {
    if (!selectedProjectId) return null;
    const snap = await snapshot.reloadSelectedProject();
    syncEnrichmentFromSnapshot(selectedProjectId, snap);
    return snap;
  }, [selectedProjectId, snapshot, syncEnrichmentFromSnapshot]);

  const reloadProjectAndSync = useCallback(async () => {
    const snap = await reloadProjectSnapshot();
    await projectsState.refresh();
    return snap;
  }, [projectsState, reloadProjectSnapshot]);

  useEffect(() => {
    const onPopState = () => setRoute(parseV10Route(window.location.search));
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 3200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const refreshScans = useCallback(async () => {
    setScansLoading(true);
    setScansError(null);
    try {
      const list = await api.listPublicScanOpportunities();
      setScanOpportunities(list.opportunities);
    } catch (error) {
      setScanOpportunities([]);
      setScansError(formatWorkflowError(error));
    } finally {
      setScansLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (route.view !== "scans") return;
    void refreshScans();
  }, [refreshScans, route.view]);

  const applyRoute = useCallback((patch: V10RoutePatch, source: "user" | "auto") => {
    if (source === "user" && patch.section !== undefined) {
      userPickedSectionRef.current = true;
    }
    setRoute((current) => pushV10Route(current, patch));
  }, []);

  const onNavigate = useCallback((patch: V10RoutePatch) => {
    applyRoute(patch, "user");
  }, [applyRoute]);

  const onNavigateAuto = useCallback((patch: V10RoutePatch) => {
    applyRoute(patch, "auto");
  }, [applyRoute]);

  const onOpenProject = useCallback(
    (patch: V10RoutePatch) => {
      userPickedSectionRef.current = false;
      lastSuggestedSectionRef.current = null;
      onNavigateAuto(patch);
    },
    [onNavigateAuto],
  );

  const intakeSubmitted =
    snapshot.intakeSummary?.status === "SUBMITTED" ||
    snapshot.clientReview?.clientLink?.status === "INTAKE_SUBMITTED";
  const hasPreview = resolveStudioHasPreview({
    clientReview: snapshot.clientReview,
    runs: snapshot.runs,
  });

  const phaseState = useMemo(() => {
    if (!selectedProjectId || !snapshot.lifecycle) return null;
    const facts = deriveSimpleProjectFacts({
      intakeSummary: snapshot.intakeSummary,
      activeGraph: snapshot.activeGraph,
      businessName: snapshot.businessName,
    });
    const base = mapLifecycleViewToPhaseState(snapshot.lifecycle, facts);

    // Slice D owns the Studio hero whenever an active safe-edit attempt owns the moment, so an
    // unrelated latest project GenerationRun cannot control the primary Agency status.
    const safeEditAgency =
      snapshot.safeEditPresentation?.ownsMoment === true
        ? snapshot.safeEditPresentation.presentation
        : null;
    if (safeEditAgency) {
      return heroFromAgencyPresentation(base, safeEditAgency);
    }

    const latest = latestRun(snapshot.runs);
    const latestPreview = snapshot.clientReview?.latestPreview ?? null;
    const review = snapshot.clientReview;
    const change = review?.latestChangeRequest ?? null;
    const phase = snapshot.lifecycle.simplePhase;

    // Prefer Slice C when review/approval/delivery owns the moment.
    const reviewOwns =
      phase === "preview_live" ||
      phase === "handoff_ready" ||
      Boolean(change) ||
      Boolean(review?.latestApproval) ||
      review?.clientLink?.status === "CHANGE_REQUESTED" ||
      review?.clientLink?.status === "APPROVAL_PENDING" ||
      review?.clientLink?.status === "APPROVED";

    if (reviewOwns) {
      const reviewPaired = derivePairedReviewApprovalDelivery({
        projectId: selectedProjectId,
        latestPreview: latestPreview
          ? {
              previewVersionId: latestPreview.previewVersionId,
              previewStatus: "READY" as const,
              updatedAt: latestPreview.updatedAt,
            }
          : null,
        latestApproval: review?.latestApproval
          ? {
              approvalId: review.latestApproval.approvalId,
              approvedByName: review.latestApproval.approvedByName ?? null,
              approvedAt: review.latestApproval.approvedAt,
            }
          : null,
        approvalMatchesLatestPassingArtifact: review?.approvalMatchesLatestPassingArtifact ?? null,
        latestChangeRequest: change
          ? {
              projectId: selectedProjectId,
              changeRequestId: change.changeRequestId,
              status: change.status,
              messagePreview: change.messagePreview ?? null,
              createdAt: change.createdAt,
              startedAt: change.startedAt ?? null,
              resolvedAt: change.resolvedAt ?? null,
              resolvedPreviewVersionId: change.resolvedPreviewVersionId ?? null,
              resolutionMatchesCurrentPassingArtifact:
                change.resolutionMatchesCurrentPassingArtifact ?? null,
              latestPreviewVersionId: latestPreview?.previewVersionId ?? null,
            }
          : null,
        departureKit: snapshot.departureKit
          ? {
              issuanceStatus: snapshot.departureKit.issuanceStatus,
              downloadAvailable: Boolean(snapshot.departureKit.downloadAvailable),
            }
          : null,
        clientLinkStatus: review?.clientLink?.status ?? null,
        consentAvailable:
          review?.clientLink?.status === "PREVIEW_READY" ||
          review?.clientLink?.status === "APPROVAL_PENDING",
      });
      // preview_live can precede published LPA — do not let Slice C source_unknown
      // suppress Slice B build/QA titles when there is no review material yet.
      const reviewMaterialPresent =
        Boolean(latestPreview) || Boolean(review?.latestApproval) || Boolean(change);
      if (reviewPaired && (reviewPaired.outcome !== "source_unknown" || reviewMaterialPresent)) {
        return heroFromAgencyPresentation(base, reviewPaired.agency);
      }
    }

    // Slice B for build / QA / exact preview moments.
    const buildQa = derivePairedBuildQaPreview({
      projectId: selectedProjectId,
      lifecycle: {
        simplePhase: snapshot.lifecycle.simplePhase,
        primaryActionId: snapshot.lifecycle.primaryActionId,
        flags: {
          generating: snapshot.lifecycle.flags.generating,
          qaFailure: snapshot.lifecycle.flags.qaFailure,
          generationLock: snapshot.lifecycle.flags.generationLock,
          staleGeneration: snapshot.lifecycle.flags.staleGeneration,
          hasPreview: snapshot.lifecycle.flags.hasPreview,
        },
      },
      latestRun: latest
        ? {
            status: latest.status,
            updatedAt: latest.updatedAt,
            createdAt: latest.createdAt,
            artifactSetHashPrefix: latest.artifactSetHashPrefix ?? "",
          }
        : null,
      qaRollup: latest?.qaRollup ?? null,
      latestPreview: latestPreview
        ? {
            previewVersionId: latestPreview.previewVersionId,
            previewStatus: "READY" as const,
            updatedAt: latestPreview.updatedAt,
          }
        : null,
      presentedPreviewVersionId: latestPreview?.previewVersionId ?? null,
      clientLinkStatus: review?.clientLink?.status ?? null,
    });
    if (!buildQa) return base;
    return heroFromAgencyPresentation(base, buildQa.agency);
  }, [
    selectedProjectId,
    snapshot.lifecycle,
    snapshot.intakeSummary,
    snapshot.activeGraph,
    snapshot.businessName,
    snapshot.runs,
    snapshot.clientReview,
    snapshot.departureKit,
    snapshot.safeEditPresentation,
  ]);

  const liveCarePresentation = useMemo((): CanonicalPresentationModelV1 | null => {
    if (!selectedProjectId) return null;
    // Client safe-edit truth wins: when a client attempt owns the moment, the live-care surface
    // shows the server-resolved Slice D Agency projection, not a separate agency-editor phase.
    if (snapshot.safeEditPresentation?.ownsMoment === true) {
      return snapshot.safeEditPresentation.presentation;
    }
    const latestAttempt = snapshot.agencyEditorPatchAttempts[0] ?? null;
    if (!latestAttempt) return null;
    const paired = derivePairedSafeEditPresentation({
      projectId: selectedProjectId,
      agencyPhase: {
        phase: latestAttempt.phase,
        attemptId: latestAttempt.attemptId,
        patchId: latestAttempt.patchId,
      },
      hasHistory: snapshot.agencyEditorPatchAttempts.length > 0,
      safeUpdateAvailable: false,
    });
    return paired?.agency ?? null;
  }, [selectedProjectId, snapshot.agencyEditorPatchAttempts, snapshot.safeEditPresentation]);

  const hasClientApproval = Boolean(snapshot.clientReview?.latestApproval);

  const selectedProjectLock = useMemo(() => {
    if (!selectedProjectId || !projectsState.data) return false;
    const summary = projectsState.data.find((row) => row.projectId === selectedProjectId);
    return summary?.generationLock?.locked ?? false;
  }, [projectsState.data, selectedProjectId]);

  const showGenerationLockBanner =
    route.view === "project" &&
    (phaseState?.phase === "generating" || (selectedProjectLock && snapshot.lifecycle?.flags.generating));

  const suggestedSection = phaseState
    ? deriveV10SuggestedSection(phaseState.phase, snapshot.clientReview)
    : "overview";

  const sectionProgress = phaseState
    ? deriveV10SectionProgress(phaseState, snapshot.clientReview)
    : null;

  const previewVersionId = snapshot.clientReview?.latestPreview?.previewVersionId ?? null;

  useEffect(() => {
    if (!selectedProjectId || !hasPreview || !previewVersionId) {
      if (!hasPreview) {
        setStudioPreviewHtml(null);
        setPreviewError(null);
        setPreviewRoutes(["/"]);
        setActivePreviewRoute("/");
      }
      return;
    }
    let cancelled = false;
    void api
      .getProjectPreview(selectedProjectId, activePreviewRoute)
      .then((preview) => {
        if (!cancelled) {
          setStudioPreviewHtml(preview.previewHtml);
          setPreviewRoutes(preview.availableRoutes);
          setPreviewError(null);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setStudioPreviewHtml(null);
          setPreviewError(formatWorkflowError(error));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [activePreviewRoute, api, hasPreview, previewVersionId, selectedProjectId]);

  useEffect(() => {
    if (!selectedProjectId) {
      setQaWarnings([]);
      setQaIssues([]);
      setPackageResolution(null);
      return;
    }
    const latest = latestRun(snapshot.runs);
    if (!latest) {
      setQaWarnings([]);
      setQaIssues([]);
      setPackageResolution(null);
      return;
    }
    const shouldLoadReasons =
      latest.status === "QA_PASSED_WITH_WARNINGS" ||
      latest.status === "QA_FAILED" ||
      latest.status === "FAILED";
    let cancelled = false;
    void api
      .getRunDetail(selectedProjectId, latest.runId)
      .then((detail) => {
        if (cancelled) return;
        setPackageResolution(detail.packageResolution ?? null);
        if (detail.packageResolution?.downgradeApplied) {
          generationOptionsRef.current.sitePackageId =
            detail.packageResolution.agencyRequestedSitePackageId;
          setRequestedSitePackageId(detail.packageResolution.agencyRequestedSitePackageId);
          if (selectedProjectId) {
            explicitPackageForProjectRef.current = selectedProjectId;
          }
        }
        if (!shouldLoadReasons) {
          setQaWarnings([]);
          setQaIssues([]);
          return;
        }
        const issues = detail.qaIssues ?? [];
        if (issues.length > 0) {
          setQaIssues(issues);
          setQaWarnings(detail.qaReasons ?? issues.map((issue) => issue.message));
          return;
        }
        const reasons = detail.qaReasons ?? [];
        if (reasons.length > 0) {
          setQaIssues([]);
          setQaWarnings(reasons);
          return;
        }
        if (detail.safeFailure?.message) {
          setQaIssues([]);
          setQaWarnings([detail.safeFailure.message]);
          return;
        }
        setQaIssues([]);
        setQaWarnings([]);
      })
      .catch(() => {
        if (!cancelled) {
          setQaIssues([]);
          setQaWarnings([]);
          setPackageResolution(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, selectedProjectId, snapshot.runs]);

  useEffect(() => {
    if (!selectedProjectId) {
      setImportQueueAvailable(false);
      return;
    }
    let cancelled = false;
    void api
      .listImportConfirmationQueue()
      .then((queue) => {
        if (!cancelled) {
          setImportQueueAvailable(queue.entries.length > 0);
        }
      })
      .catch(() => {
        if (!cancelled) setImportQueueAvailable(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, selectedProjectId, snapshot.runs]);

  const latestRunRow = latestRun(snapshot.runs);
  const builtSitePackageId =
    authoritativeGraphPackageId ??
    (latestRunRow && projectPackageSnapshotReady
      ? sitePackageIdFromRecipeId(latestRunRow.recipeId)
      : null);

  const sitePackageId = builtSitePackageId ?? requestedSitePackageId ?? null;

  useEffect(() => {
    if (!selectedProjectId || route.view !== "project") return;
    if (userPickedSectionRef.current) return;
    if (route.section === suggestedSection) {
      lastSuggestedSectionRef.current = suggestedSection;
      return;
    }
    if (lastSuggestedSectionRef.current === suggestedSection) return;
    lastSuggestedSectionRef.current = suggestedSection;
    onNavigateAuto(
      v10ProjectRoute({ projectId: selectedProjectId, section: suggestedSection, mode: route.mode ?? undefined }),
    );
  }, [onNavigateAuto, route.mode, route.section, route.view, selectedProjectId, suggestedSection]);

  useEffect(() => {
    if (!selectedProjectId) return;
    const generating = snapshot.lifecycle?.flags.generating ?? false;
    const packaging = snapshot.lifecycle?.flags.packaging ?? false;
    const awaitingClientIntake =
      Boolean(snapshot.clientReview?.clientLink?.status?.startsWith("INTAKE")) &&
      snapshot.clientReview?.clientLink?.status !== "INTAKE_SUBMITTED";
    const awaitingPreviewHtml = hasPreview && !studioPreviewHtml && !previewError;
    const fastPoll = fastPollUntil !== null && Date.now() < fastPollUntil;
    const handoffFastPoll =
      fastPoll && hasClientApproval && !isHandoffReadyDepartureKit(snapshot.departureKit);
    if (!generating && !packaging && !awaitingPreviewHtml && !handoffFastPoll && !awaitingClientIntake) return;
    const intervalMs =
      fastPoll || generating || packaging || awaitingPreviewHtml || awaitingClientIntake
        ? (snapshot.lifecycle?.pollHints.suggestedIntervalMs ?? 2000)
        : config.pollIntervalMs;
    const id = window.setInterval(() => {
      void reloadProjectAndSync();
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [
    config.pollIntervalMs,
    fastPollUntil,
    hasClientApproval,
    hasPreview,
    previewError,
    reloadProjectAndSync,
    selectedProjectId,
    snapshot.clientReview?.clientLink?.status,
    snapshot.departureKit,
    snapshot.lifecycle?.flags.generating,
    snapshot.lifecycle?.flags.packaging,
    snapshot.lifecycle?.pollHints.suggestedIntervalMs,
    studioPreviewHtml,
  ]);

  useEffect(() => {
    if (!isHandoffReadyDepartureKit(snapshot.departureKit)) return;
    if (fastPollUntil === null) return;
    setFastPollUntil(null);
    setToast("Handoff ready — download your Departure Kit.");
  }, [fastPollUntil, snapshot.departureKit]);

  const previewResolution = useMemo(
    () =>
      resolveSharePreviewUrl({
        clientReview: snapshot.clientReview,
        lastClientLink,
        storedSession: selectedProjectId ? readClientLinkSession(selectedProjectId) : null,
        config,
      }),
    [config, lastClientLink, selectedProjectId, snapshot.clientReview],
  );
  const previewUrl = previewResolution.kind === "ready" ? previewResolution.url : null;

  const previewOpenGuardInput = useMemo((): PreviewOpenGuardInput => ({
    previewHtml: studioPreviewHtml,
    previewUrl,
    previewVersionId,
    qaFailure: phaseState?.qaFailure ?? false,
    latestRunStatus: latestRunRow?.status ?? null,
  }), [
    latestRunRow?.status,
    phaseState?.qaFailure,
    previewUrl,
    previewVersionId,
    studioPreviewHtml,
  ]);

  const clientShareGuardInput = useMemo((): ClientShareGuardInput => ({
    latestRunStatus: latestRunRow?.status ?? null,
    qaFailure: phaseState?.qaFailure ?? false,
    warnShareAcknowledged,
  }), [latestRunRow?.status, phaseState?.qaFailure, warnShareAcknowledged]);

  useEffect(() => {
    if (!isQaWarnRunStatus(latestRunRow?.status ?? null)) {
      setWarnShareAcknowledged(false);
    }
  }, [latestRunRow?.status]);

  const portalUrl = useMemo(() => {
    if (previewUrl) return previewUrl;
    const session = selectedProjectId ? readClientLinkSession(selectedProjectId) : null;
    if (session) return clientLinkUrl(config, session.shellPath);
    if (lastClientLink) return clientLinkUrl(config, lastClientLink.shellPath);
    const base = config.clientPortalBaseUrl.trim();
    return base.length > 0 ? base : null;
  }, [config, lastClientLink, previewUrl, selectedProjectId]);

  const queueSections = useMemo(
    () =>
      deriveV10QueueSections({
        projects: projectsState.data ?? [],
        reviews: enrichment.projectReviews,
        departureKits: enrichment.projectDepartureKits,
        businessNames: enrichment.projectBusinessNames,
        lifecycleSummaries: enrichment.projectLifecycleSummaries,
        ownerLabels: Object.fromEntries(
          Object.entries(enrichment.projectLifecycleSummaries).map(([projectId, summary]) => [
            projectId,
            summary?.ownerLabel ?? null,
          ]),
        ),
      }),
    [enrichment, projectsState.data],
  );

  const startHere = useMemo(() => {
    const projects = projectsState.data ?? [];
    const ux006Candidates: AgencyTodayStartHereInput[] = projects.map((project) => {
      const review = enrichment.projectReviews[project.projectId] ?? null;
      const lifecycle = enrichment.projectLifecycleSummaries[project.projectId] ?? null;
      return {
        projectId: project.projectId,
        businessName: enrichment.projectBusinessNames[project.projectId] ?? null,
        clientDisplayName: review?.clientLink?.clientDisplayName ?? null,
        clientReview: review
          ? {
              projectId: review.projectId,
              clientLink: review.clientLink
                ? { status: review.clientLink.status }
                : null,
            }
          : null,
        lifecycle: lifecycle
          ? {
              projectId: lifecycle.projectId,
              simplePhase: lifecycle.simplePhase,
              primaryActionId: lifecycle.primaryActionId,
              flags: {
                qaFailure: lifecycle.flags.qaFailure,
                generating: lifecycle.flags.generating,
                packaging: lifecycle.flags.packaging,
                generationLock: lifecycle.flags.generationLock,
              },
            }
          : null,
      };
    });
    const sliceACandidates: AccessIntakeReadinessPresentationInput[] = projects.map((project) => {
      const review = enrichment.projectReviews[project.projectId] ?? null;
      const lifecycle = enrichment.projectLifecycleSummaries[project.projectId] ?? null;
      return {
        projectId: project.projectId,
        businessName: enrichment.projectBusinessNames[project.projectId] ?? null,
        clientDisplayName: review?.clientLink?.clientDisplayName ?? null,
        clientLink: review?.clientLink
          ? { status: review.clientLink.status, accessReady: true }
          : null,
        lifecycle: lifecycle
          ? {
              projectId: lifecycle.projectId,
              simplePhase: lifecycle.simplePhase,
              primaryActionId: lifecycle.primaryActionId,
              flags: {
                qaFailure: lifecycle.flags.qaFailure,
                generating: lifecycle.flags.generating,
                packaging: lifecycle.flags.packaging,
                generationLock: lifecycle.flags.generationLock,
              },
            }
          : null,
        projectExists: true,
      };
    });
    const ux006 = selectAgencyTodayStartHere(ux006Candidates);
    const sliceA = selectAgencyAccessIntakeReadinessStartHere(sliceACandidates);
    // Prefer Slice A NEEDS_ATTENTION (blockers). Agency READY Start Here stays ENR-UX-006-gated.
    if (sliceA?.condition === "NEEDS_ATTENTION") return sliceA;
    if (ux006) return ux006;
    // Slice A WAITING / non-READY only — never substitute Slice A READY for a failed UX-006 gate.
    if (sliceA && sliceA.condition !== "READY") return sliceA;
    return null;
  }, [enrichment, projectsState.data]);

  const handleStartHereAction = useCallback(
    (action: CanonicalPrimaryAction) => {
      if (action.intent !== "OPEN_AGENCY_PROJECT_TASK") return;
      if (action.task === "REVIEW_BUSINESS_INFORMATION") {
        onOpenProject(v10ProjectRoute({ projectId: action.projectId, section: "business" }));
        return;
      }
      if (action.task === "OPEN_CLIENT_LINK") {
        onOpenProject(v10ProjectRoute({ projectId: action.projectId, section: "overview" }));
        return;
      }
      if (action.task === "REVIEW_CLARIFICATION") {
        onOpenProject(v10ProjectRoute({ projectId: action.projectId, section: "overview" }));
        return;
      }
      if (action.task === "REVIEW_READINESS") {
        onOpenProject(v10ProjectRoute({ projectId: action.projectId, section: "business" }));
      }
    },
    [onOpenProject],
  );

  const projectListItems = useMemo(
    () =>
      (projectsState.data ?? []).map((project) =>
        mapProjectSummaryToListItem({
          project,
          businessName: enrichment.projectBusinessNames[project.projectId] ?? null,
          review: enrichment.projectReviews[project.projectId],
          departureKit: enrichment.projectDepartureKits[project.projectId],
          lifecycleSummary: enrichment.projectLifecycleSummaries[project.projectId],
          ownerLabel: enrichment.projectLifecycleSummaries[project.projectId]?.ownerLabel ?? null,
        }),
      ),
    [enrichment, projectsState.data],
  );

  useEffect(() => {
    if (route.view !== "project" || !selectedProjectId || route.mode) return;
    const item = projectListItems.find((project) => project.id === selectedProjectId);
    if (!item) return;
    onNavigateAuto(
      v10ProjectRoute({
        projectId: selectedProjectId,
        section: route.section ?? undefined,
        mode: item.mode,
      }),
    );
  }, [onNavigateAuto, projectListItems, route.mode, route.section, route.view, selectedProjectId]);

  const inboxRows = useMemo(
    () =>
      deriveV10InboxRows({
        projects: projectsState.data ?? [],
        reviews: enrichment.projectReviews,
        businessNames: enrichment.projectBusinessNames,
      }),
    [enrichment, projectsState.data],
  );

  const scanRows = useMemo(() => scanOpportunities.map(mapScanOpportunityToRow), [scanOpportunities]);

  const portfolioSignals = useMemo(
    () =>
      deriveCommandCenterStats({
        missionSummary: missionControlState.data,
        scanOpportunityCount: scanOpportunitiesState.data ?? 0,
      }).map((stat) => ({ label: stat.label, value: String(stat.count), navigateTo: stat.navigateTo })),
    [missionControlState.data, scanOpportunitiesState.data],
  );

  const copyClientLink = useCallback(
    async (projectIdOverride?: string, options?: { readonly previewShareContext?: boolean }) => {
      const pid = projectIdOverride ?? selectedProjectId;
      if (!pid) {
        setToast("Select a project first.");
        return;
      }
      // Gate only when sharing from preview/share context — intake/enrich copy stays ungated.
      if (options?.previewShareContext) {
        const shareBlock = getClientShareBlockReason(clientShareGuardInput);
        if (shareBlock) {
          setToast(shareBlock);
          return;
        }
      }
      try {
        const name = enrichment.projectBusinessNames[pid] ?? snapshot.businessName;
        const result = await api.createClientLink(pid, { clientDisplayName: name ?? undefined });
        setLastClientLink(result);
        saveClientLinkSession(pid, result);
        const url = clientLinkUrl(config, result.shellPath);
        await navigator.clipboard.writeText(url);
        setToast(
          options?.previewShareContext && isQaWarnRunStatus(clientShareGuardInput.latestRunStatus)
            ? "Client link copied — preview has unresolved quality warnings; not client-ready."
            : "Client link copied.",
        );
        if (pid === selectedProjectId) await reloadProjectAndSync();
      } catch (error) {
        setToast(formatWorkflowError(error));
      }
    },
    [
      api,
      clientShareGuardInput,
      config,
      enrichment.projectBusinessNames,
      reloadProjectAndSync,
      selectedProjectId,
      snapshot.businessName,
    ],
  );

  const copyPreviewLink = useCallback(async () => {
    const blockReason = getPreviewOpenBlockReason(previewOpenGuardInput);
    if (blockReason) {
      setToast(blockReason);
      return;
    }
    const shareBlock = getClientShareBlockReason(clientShareGuardInput);
    if (shareBlock) {
      setToast(shareBlock);
      return;
    }
    if (previewUrl) {
      await navigator.clipboard.writeText(previewUrl);
      setToast(
        isQaWarnRunStatus(clientShareGuardInput.latestRunStatus)
          ? "Preview link copied — not client-ready until quality issues are resolved."
          : "Preview link copied.",
      );
      return;
    }
    setToast("Open preview in browser — client link shares the live preview.");
  }, [clientShareGuardInput, previewOpenGuardInput, previewUrl]);

  const openPreview = useCallback(() => {
    logPreviewOpenGuardDebug("v10-open", previewOpenGuardInput);
    const blockReason = getPreviewOpenBlockReason(previewOpenGuardInput);
    if (blockReason) {
      setToast(blockReason);
      return;
    }
    if (previewUrl) {
      window.open(previewUrl, "_blank", "noopener,noreferrer");
      return;
    }
    if (studioPreviewHtml && selectedProjectId) {
      const opened = openBlobPreviewInNewTab(studioPreviewHtml, window);
      if (!opened) {
        setToast(PREVIEW_UNAVAILABLE_MESSAGE);
      }
      return;
    }
    setToast(PREVIEW_UNAVAILABLE_MESSAGE);
  }, [previewOpenGuardInput, previewUrl, selectedProjectId, studioPreviewHtml]);

  const handleConfirmBuild = useCallback(async () => {
    if (!selectedProjectId) return;
    if (
      !isProjectPackageSnapshotReady({
        selectedProjectId,
        lastSyncedProjectId,
        projectLoading: snapshot.projectLoading,
        graphProjectId: snapshot.activeGraph?.businessGraph.projectId,
      })
    ) {
      setToast("Loading selected project…");
      return;
    }
    const builtSitePackageId = generationOptionsRef.current.sitePackageId;
    const agencyRequestedSitePackageId = resolveAgencyRequestedForBuild({
      builtSitePackageId,
      requestedSitePackageId,
    });
    setWorkflowBusy(true);
    setValidationIssues([]);
    try {
      const result = await confirmFactsAndBuildPreview(api, selectedProjectId, {
        intakeSummary: snapshot.intakeSummary,
        latestDraftId: snapshot.bgStatus?.latestDraftId ?? null,
        currentBusinessGraphRevisionId: snapshot.bgStatus?.currentBusinessGraphRevisionId ?? null,
        sitePackageId: builtSitePackageId,
        agencyRequestedSitePackageId,
        lensId: generationOptionsRef.current.lensId,
        intakeSubmitted,
        clientLinkId:
          snapshot.intakeSummary?.clientLinkId ??
          snapshot.clientReview?.clientLink?.clientLinkId ??
          null,
      });
      if (result.kind === "validation_blocked") {
        setValidationIssues(result.issues.length > 0 ? result.issues : ["Validation failed — fix facts before building."]);
        setToast("Fix validation issues before building preview.");
        return;
      }
      if (result.kind === "package_eligibility_blocked") {
        setPackageEligibilityBlocked(result.blocked);
        setToast(
          `Profile is not ready for ${sitePackageDisplayLabel(result.blocked.requestedPackage)} — see options below.`,
        );
        if (selectedProjectId) {
          onNavigateAuto(
            v10ProjectRoute({ projectId: selectedProjectId, section: "business", mode: route.mode ?? undefined }),
          );
        }
        return;
      }
      setPackageEligibilityBlocked(null);
      setToast("Generating website…");
      setFastPollUntil(Date.now() + 120_000);
      userPickedSectionRef.current = false;
      lastSuggestedSectionRef.current = null;
      await reloadProjectAndSync();
      if (selectedProjectId) {
        onNavigateAuto(v10ProjectRoute({ projectId: selectedProjectId, section: "website", mode: route.mode ?? undefined }));
      }
    } catch (error) {
      setToast(formatWorkflowError(error));
    } finally {
      setWorkflowBusy(false);
    }
  }, [
    api,
    intakeSubmitted,
    lastSyncedProjectId,
    onNavigateAuto,
    reloadProjectAndSync,
    requestedSitePackageId,
    route.mode,
    selectedProjectId,
    snapshot.activeGraph?.businessGraph.projectId,
    snapshot.bgStatus,
    snapshot.clientReview?.clientLink?.clientLinkId,
    snapshot.intakeSummary,
    snapshot.projectLoading,
  ]);

  const handleBuildWithPackage = useCallback(
    async (sitePackageId: string) => {
      if (!selectedProjectId) return;
      if (
        !isProjectPackageSnapshotReady({
          selectedProjectId,
          lastSyncedProjectId,
          projectLoading: snapshot.projectLoading,
          graphProjectId: snapshot.activeGraph?.businessGraph.projectId,
        })
      ) {
        setToast("Loading selected project…");
        return;
      }
      const agencyRequestedSitePackageId = resolveAgencyRequestedForBuild({
        builtSitePackageId: sitePackageId,
        requestedSitePackageId,
        eligibilityRequestedPackage: packageEligibilityBlocked?.requestedPackage,
      });
      generationOptionsRef.current.sitePackageId = sitePackageId;
      explicitPackageForProjectRef.current = selectedProjectId;
      setRequestedSitePackageId(agencyRequestedSitePackageId ?? sitePackageId);
      setWorkflowBusy(true);
      setValidationIssues([]);
      try {
        const result = await confirmFactsAndBuildPreview(api, selectedProjectId, {
          intakeSummary: snapshot.intakeSummary,
          latestDraftId: snapshot.bgStatus?.latestDraftId ?? null,
          currentBusinessGraphRevisionId: snapshot.bgStatus?.currentBusinessGraphRevisionId ?? null,
          sitePackageId,
          agencyRequestedSitePackageId,
          lensId: generationOptionsRef.current.lensId,
          intakeSubmitted,
          clientLinkId:
            snapshot.intakeSummary?.clientLinkId ??
            snapshot.clientReview?.clientLink?.clientLinkId ??
            null,
        });
        if (result.kind === "validation_blocked") {
          setValidationIssues(result.issues.length > 0 ? result.issues : ["Validation failed — fix facts before building."]);
          setToast("Fix validation issues before building preview.");
          return;
        }
        if (result.kind === "package_eligibility_blocked") {
          setPackageEligibilityBlocked(result.blocked);
          setToast(
            `Profile is not ready for ${sitePackageDisplayLabel(result.blocked.requestedPackage)} — see options below.`,
          );
          return;
        }
        setPackageEligibilityBlocked(null);
        setToast(`Generating ${sitePackageDisplayLabel(sitePackageId)} site…`);
        setFastPollUntil(Date.now() + 120_000);
        userPickedSectionRef.current = false;
        lastSuggestedSectionRef.current = null;
        await reloadProjectAndSync();
        onNavigateAuto(
          v10ProjectRoute({ projectId: selectedProjectId, section: "website", mode: route.mode ?? undefined }),
        );
      } catch (error) {
        setToast(formatWorkflowError(error));
      } finally {
        setWorkflowBusy(false);
      }
    },
    [
      api,
      intakeSubmitted,
      lastSyncedProjectId,
      onNavigateAuto,
      packageEligibilityBlocked?.requestedPackage,
      reloadProjectAndSync,
      requestedSitePackageId,
      route.mode,
      selectedProjectId,
      snapshot.activeGraph?.businessGraph.projectId,
      snapshot.bgStatus,
      snapshot.clientReview?.clientLink?.clientLinkId,
      snapshot.intakeSummary,
      snapshot.projectLoading,
    ],
  );

  const handleEnrichProfile = useCallback(() => {
    if (!selectedProjectId) return;
    onNavigateAuto(
      v10ProjectRoute({ projectId: selectedProjectId, section: "business", mode: route.mode ?? undefined }),
    );
    void copyClientLink();
    setToast("Share the client link so your client can add more business details.");
  }, [copyClientLink, onNavigateAuto, route.mode, selectedProjectId]);

  const handlePackageHandoff = useCallback(async () => {
    if (!selectedProjectId || !phaseState?.exportRunId) {
      setToast("Export run is not ready yet.");
      return;
    }
    setWorkflowBusy(true);
    setHandoffError(null);
    try {
      await api.requestRunExport(phaseState.exportRunId, selectedProjectId);
      setToast("Packaging handoff…");
      setFastPollUntil(Date.now() + 180_000);
      await reloadProjectAndSync();
    } catch (error) {
      const message = formatWorkflowError(error);
      setHandoffError(message);
      setToast(message);
    } finally {
      setWorkflowBusy(false);
    }
  }, [api, phaseState?.exportRunId, reloadProjectAndSync, selectedProjectId]);

  const handleDownloadHandoff = useCallback(async () => {
    if (!selectedProjectId) return;
    setWorkflowBusy(true);
    setHandoffError(null);
    try {
      const blob = await api.downloadDepartureKit(selectedProjectId);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `departure-kit-${selectedProjectId}.zip`;
      anchor.click();
      URL.revokeObjectURL(url);
      setToast("Departure Kit download started.");
    } catch (error) {
      const message = formatWorkflowError(error);
      setHandoffError(message);
      setToast(message);
    } finally {
      setWorkflowBusy(false);
    }
  }, [api, selectedProjectId]);

  const handlePrimaryAction = useCallback(
    (actionId: string) => {
      if (actionId === "confirm-build") void handleConfirmBuild();
      else if (actionId === "client-link") void copyClientLink();
      else if (actionId === "copy-preview") {
        if (shouldOverrideHeroForQaWarn(clientShareGuardInput)) {
          if (selectedProjectId) {
            onNavigateAuto(
              v10ProjectRoute({
                projectId: selectedProjectId,
                section: "business",
                mode: route.mode ?? undefined,
              }),
            );
          }
          setToast(CLIENT_SHARE_QA_WARN_PRIMARY_CTA + " — review Truth / Proof before sharing.");
          return;
        }
        void copyPreviewLink();
      } else if (actionId === "resolve-quality-issues") {
        if (selectedProjectId) {
          onNavigateAuto(
            v10ProjectRoute({
              projectId: selectedProjectId,
              section: "business",
              mode: route.mode ?? undefined,
            }),
          );
        }
      } else if (actionId === "package-handoff") void handlePackageHandoff();
      else if (actionId === "download-handoff") void handleDownloadHandoff();
      else if (actionId === "open-live-care") {
        if (selectedProjectId) {
          onNavigateAuto(
            v10ProjectRoute({
              projectId: selectedProjectId,
              section: "overview",
              mode: route.mode ?? undefined,
            }),
          );
        }
      }
    },
    [
      clientShareGuardInput,
      copyClientLink,
      copyPreviewLink,
      handleConfirmBuild,
      handleDownloadHandoff,
      handlePackageHandoff,
      onNavigateAuto,
      route.mode,
      selectedProjectId,
    ],
  );

  const handleCreateProject = useCallback(
    async (mode: V10ProjectMode) => {
      const validationError = validateV10NewProjectForm(mode, newProjectForm);
      if (validationError) {
        setValidationIssues([validationError]);
        setToast(validationError);
        return;
      }
      setWorkflowBusy(true);
      setValidationIssues([]);
      const studioInput = v10FormToStudioInput(newProjectForm);
      try {
        let projectId: string;
        let clientUrl: string | null = null;
        if (mode === "DEFAULT_GENERATION") {
          if (newProjectForm.startMethod === "intake") {
            const result = await createIntakeLinkProject(api, config, studioInput);
            projectId = result.projectId;
            clientUrl = result.intakeUrl;
          } else {
            const result = await agencyEnterProject(api, studioInput);
            projectId = result.projectId;
          }
        } else if (mode === "EXISTING_SITE_SCAN") {
          const result = await createRelaunchScanProject(api, config, studioInput, newProjectForm.scanUrl);
          projectId = result.projectId;
          clientUrl = result.intakeUrl;
        } else {
          const result = await createExternalProofProject(api, config, studioInput, newProjectForm.externalUrl);
          projectId = result.projectId;
          clientUrl = result.intakeUrl;
        }
        if (clientUrl) {
          await navigator.clipboard.writeText(clientUrl);
          setToast("Client link copied — share with your client.");
        } else {
          setToast("Project created — confirm facts and build preview when ready.");
        }
        saveProjectDisplayName(projectId, studioInput.businessName);
        generationOptionsRef.current = {
          sitePackageId: newProjectForm.sitePackageId,
          lensId: newProjectForm.lensId,
        };
        setRequestedSitePackageId(newProjectForm.sitePackageId);
        explicitPackageForProjectRef.current = projectId;
        lastProjectForRequestedRef.current = projectId;
        enrichment.patchProjectEnrichment(projectId, { businessName: studioInput.businessName });
        setFastPollUntil(Date.now() + 60_000);
        await projectsState.refresh();
        setNewProjectForm(V10_EMPTY_NEW_PROJECT_FORM);
        userPickedSectionRef.current = false;
        lastSuggestedSectionRef.current = null;
        const initialSection =
          mode === "DEFAULT_GENERATION" && newProjectForm.startMethod === "agency" ? "business" : null;
        onOpenProject(v10ProjectRoute({ projectId, section: initialSection, mode }));
      } catch (error) {
        const message = formatWorkflowError(error);
        setValidationIssues([message]);
        setToast(message);
      } finally {
        setWorkflowBusy(false);
      }
    },
    [api, config, enrichment, newProjectForm, onOpenProject, projectsState],
  );

  const handleRunScan = useCallback(
    async (url: string) => {
      const trimmed = url.trim();
      if (!trimmed) {
        setToast("Enter a website URL to scan.");
        return;
      }
      setWorkflowBusy(true);
      try {
        const created = await api.createPublicScan(trimmed);
        let status = await api.getPublicScanStatus(created.scanId);
        const deadline = Date.now() + 120_000;
        while (status.status !== "COMPLETE" && status.status !== "FAILED" && Date.now() < deadline) {
          await new Promise((r) => setTimeout(r, 2500));
          status = await api.getPublicScanStatus(created.scanId);
        }
        if (status.status === "FAILED" || !status.publicReportRef) {
          throw new Error("Scan failed — check the URL and try again.");
        }
        await api.claimPublicReport(status.publicReportRef);
        setToast("Scan complete — opportunity claimed.");
        scanOpportunitiesState.refresh();
        await refreshScans();
      } catch (error) {
        setToast(formatWorkflowError(error));
      } finally {
        setWorkflowBusy(false);
      }
    },
    [api, refreshScans, scanOpportunitiesState],
  );

  const openRelaunchSheet = useCallback(
    (scanUrl?: string) => {
      if (scanUrl?.trim()) {
        setNewProjectForm((current) => ({ ...current, scanUrl: scanUrl.trim() }));
      }
      onNavigate(v10NewWorkSheet("EXISTING_SITE_SCAN"));
    },
    [onNavigate],
  );

  return (
    <div className="enrein-v10-ui">
      {apiHealth === "unreachable" ? (
        <div className="v10-api-health-banner" role="alert">
          API not reachable at {config.apiBaseUrl || "Vite proxy /v1"}. Start Docker Desktop, then{" "}
          <code>pnpm db:up</code> and <code>corepack pnpm dev</code> from <code>apps/api</code>.
        </div>
      ) : null}
      {previewError && route.view === "project" ? (
        <div className="v10-api-health-banner" role="alert">
          Studio preview failed: {previewError}. Ensure API <code>TIER0_ARTIFACT_ROOT</code> matches worker and restart API.
        </div>
      ) : null}
      {showGenerationLockBanner ? (
        <div className="v10-api-health-banner v10-api-health-banner--info" role="status">
          Generation in progress — concurrent builds blocked.
        </div>
      ) : null}
      {toast ? (
        <div className="v10-toast" role="status">
          {toast}
        </div>
      ) : null}
      <V10Shell
        route={route}
        onNavigate={onNavigate}
        onOpenProject={onOpenProject}
        startHere={startHere}
        onStartHereAction={handleStartHereAction}
        onSectionNavigate={onNavigate}
        workflowBusy={effectiveWorkflowBusy}
        validationIssues={validationIssues}
        qaIssues={qaIssues}
        qaWarnings={qaWarnings}
        importQueueAvailable={importQueueAvailable}
        newProjectForm={newProjectForm}
        onNewProjectFormChange={setNewProjectForm}
        onCreateProject={(mode) => void handleCreateProject(mode)}
        queueSections={queueSections}
        portfolioSignals={portfolioSignals}
        portfolioLoading={missionControlState.loading || projectsState.loading}
        portfolioError={
          missionControlState.error
            ? formatWorkflowError(missionControlState.error)
            : projectsState.error
              ? formatWorkflowError(projectsState.error)
              : null
        }
        portfolioSummary={portfolioSummary}
        portfolioSummaryLoading={portfolioSummaryLoading}
        portfolioSummaryError={portfolioSummaryError}
        onStatNavigate={(target) => {
          if (target === "scan-opportunities") onNavigate({ view: "scans", sheet: null, intake: null });
          else onNavigate({ view: "projects", sheet: null, intake: null });
        }}
        projectListItems={projectListItems}
        projectsLoading={projectsState.loading}
        projectsError={projectsState.error ? formatWorkflowError(projectsState.error) : null}
        inboxRows={inboxRows}
        scanRows={scanRows}
        scansLoading={scansLoading}
        scansError={scansError}
        onRunScan={(url) => void handleRunScan(url)}
        onOpenRelaunchSheet={openRelaunchSheet}
        deployEnv={config.deployEnv}
        configAgencyId={agencyId}
        phaseState={phaseState}
        sectionProgress={sectionProgress}
        projectLoading={snapshot.projectLoading}
        projectLoadError={snapshot.projectLoadError}
        businessName={
          selectedProjectId
            ? resolveProjectDisplayName({
                projectId: selectedProjectId,
                businessName: snapshot.businessName ?? snapshot.intakeSummary?.businessName,
                review: snapshot.clientReview,
              })
            : "Client"
        }
        intakeStatusLabel={humanIntakeStatus(snapshot.intakeSummary)}
        intakeSummary={snapshot.intakeSummary}
        changeRequestPreview={snapshot.clientReview?.latestChangeRequest?.messagePreview ?? null}
        previewUrl={previewUrl}
        previewOpenGuardInput={previewOpenGuardInput}
        clientShareGuardInput={clientShareGuardInput}
        onWarnShareAcknowledgedChange={setWarnShareAcknowledged}
        studioPreviewHtml={studioPreviewHtml}
        previewError={previewError}
        previewRoutes={previewRoutes}
        activePreviewRoute={activePreviewRoute}
        onPreviewRouteChange={setActivePreviewRoute}
        sitePackageId={sitePackageId}
        requestedSitePackageId={requestedSitePackageId}
        builtSitePackageId={builtSitePackageId}
        packageEligibilityBlocked={packageEligibilityBlocked}
        packageResolution={packageResolution}
        onBuildWithPackage={(pkg) => void handleBuildWithPackage(pkg)}
        onEnrichProfile={handleEnrichProfile}
        exportRunId={phaseState?.exportRunId ?? null}
        handoffError={handoffError}
        departureKit={snapshot.departureKit}
        liveCarePresentation={liveCarePresentation}
        onPrimaryAction={handlePrimaryAction}
        onCopyClientLink={() => void copyClientLink()}
        onShareClientLinkFromPreview={() => void copyClientLink(undefined, { previewShareContext: true })}
        onCopyPreviewLink={() => void copyPreviewLink()}
        onOpenPreview={openPreview}
        onOpenPortal={() => {
          if (!portalUrl) {
            setToast("Client portal link is not ready — copy a client link first.");
            return;
          }
          window.open(portalUrl, "_blank", "noopener,noreferrer");
        }}
        api={api}
        onImportQueueChanged={() => void reloadProjectAndSync()}
      />
    </div>
  );
}
